import { useEffect } from "react";
import { X, ExternalLink } from "lucide-react";

const ROZE_URL = "https://restoransroze.lv";

const ProductModal = ({ item, language, onClose, onAddToCart, ordersDisabled = false }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!item) return null;

  const translations = {
    addToCart: {
      en: "Add to Cart",
      ru: "Добавить",
      lv: "Pievienot grozam"
    },
    tryAtRoze: {
      en: "Try at Restaurant Roze",
      ru: "Отведать в ресторане Roze",
      lv: "Baudīt restorānā Roze"
    },
    allergens: {
      en: "Allergens",
      ru: "Аллергены",
      lv: "Alergēni"
    },
    priceTba: { en: "Price TBA", ru: "Цена уточняется", lv: "Cena tiks precizēta" },
    comingSoon: { en: "Coming soon", ru: "Скоро в продаже", lv: "Drīzumā" }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="relative bg-[#FDFBF7] rounded-none w-full max-w-[960px] max-h-[90vh] overflow-y-auto shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] grid grid-cols-1 md:grid-cols-2"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-modal-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-11 h-11 rounded-full bg-[#FDFBF7]/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors z-10 text-[#1C1917]"
          aria-label="Close"
          data-testid="product-modal-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        {/* Product Image */}
        <div className="relative aspect-[5/4] md:aspect-auto md:min-h-[520px] bg-[#F3EFE7] overflow-hidden flex items-center justify-center">
          <img
            src={item.image}
            alt={item[`name_${language}`]}
            className="absolute inset-0 w-full h-full object-contain object-center"
          />
        </div>

        {/* Product Info */}
        <div className="p-7 sm:p-10 md:p-12 flex flex-col font-body">
          <p className="rz-eyebrow m-0 mb-5">{item.category === 'pizza' ? '30 cm' : ' '}</p>
          <div className="flex items-start justify-between gap-6">
            <h2 id="product-modal-title" className="font-display text-[2rem] sm:text-[2.4rem] leading-[1.05] text-[#1C1917] m-0">
              {item[`name_${language}`]}
            </h2>
            <span className="font-display text-[1.6rem] sm:text-[1.9rem] text-[#1C1917] shrink-0 tabular-nums" data-testid="product-modal-price">
              {item.price == null ? <span className="font-body text-[0.72rem] font-medium tracking-[0.14em] uppercase text-[#78716C]">{translations.priceTba[language]}</span> : `€${item.price.toFixed(2)}`}
            </span>
          </div>

          <p className="text-[1.02rem] text-[#57534E] mt-6 mb-0 leading-[1.75]">
            {item[`description_${language}`]}
          </p>

          {/* Allergens */}
          {item.allergens && (
            <p className="text-[0.74rem] tracking-[0.1em] text-[#A8A29E] mt-5 mb-0">
              <span className="uppercase">{translations.allergens[language]}</span>: {item.allergens}
            </p>
          )}

          <div className="mt-auto pt-10">
          {/* Add to Cart Button */}
          {ordersDisabled ? (
            <a
              href={ROZE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="rz-btn w-full"
              data-testid="product-modal-roze-cta"
            >
              {translations.tryAtRoze[language]}
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : item.price == null ? (
            <p className="m-0 text-center text-[0.72rem] font-medium tracking-[0.22em] uppercase text-[#78716C] border border-[#E7E2D9] py-4" data-testid="product-modal-coming-soon">
              {translations.comingSoon[language]}
            </p>
          ) : (
            <button
              onClick={() => {
                onAddToCart(item);
                onClose();
              }}
              className="rz-btn w-full"
              data-testid="product-modal-add-to-cart"
            >
              + {translations.addToCart[language]}
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
